import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getPageBySlug } from "../../../services/pagesServices";

import {
  applySeo,
  isExternalUrl,
  normalizeRedirectUrl,
  resolvePageImage,
} from "../utils/publicPageUtils";

export function usePublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const locale = "fr";

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getPageBySlug(slug, { locale });

      const redirectUrl = normalizeRedirectUrl(result?.redirect_url);

      if (redirectUrl) {
        if (isExternalUrl(redirectUrl)) {
          window.location.replace(redirectUrl);
          return;
        }

        navigate(redirectUrl, { replace: true });
        return;
      }

      setPage(result || null);
    } catch (err) {
      console.error("getPageBySlug error:", err);
      setPage(null);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, [locale, navigate, slug]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const result = await getPageBySlug(slug, { locale });

        if (!mounted) return;

        const redirectUrl = normalizeRedirectUrl(result?.redirect_url);

        if (redirectUrl) {
          if (isExternalUrl(redirectUrl)) {
            window.location.replace(redirectUrl);
            return;
          }

          navigate(redirectUrl, { replace: true });
          return;
        }

        setPage(result || null);
      } catch (err) {
        console.error("getPageBySlug error:", err);

        if (mounted) {
          setPage(null);
          setError("LOAD_ERROR");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [locale, navigate, slug]);

  useEffect(() => {
    if (!page) return;
    applySeo(page);
  }, [page]);

  const heroImage = useMemo(() => {
    return resolvePageImage(page?.cover_image_url);
  }, [page?.cover_image_url]);

  const isUnavailable = !page || page.is_active === false;

  return {
    loading,
    error,
    page,
    heroImage,
    isUnavailable,
    refresh: loadPage,
  };
}